import cx from "classnames";
import { ComponentProps, ReactElement, ReactNode } from "react";
import SocialIcons, {
  SocialIconProps,
} from "../../Block/SocialIcon/SocialIcon";
import SponsorshipBox from "../../Block/SponsorshipBox/SponsorshipBox";
import Button from "../../Element/Button";
import CloseIcon from "../../Element/CloseIcon/CloseIcon";
import Divider from "../../Element/Divider/divider";
import ResponsiveImage from "../../Element/ResponsiveImage";
import * as Typography from "../../Element/Typography";
import { GlobalBaseData } from "../../Layout/Base/types";
import { ImageCrop, UserSubmissionBox } from "../../Utility/dataTypes";
import { placeholderCrops } from "../../Utility/placeholder";
import styles from "./megamenu.module.css";
import { useCustomClassContext } from "../../../hooks";

const { Body, Heading } = Typography;

type Items = { text: string; url: string; hideInMegaMenu?: boolean };

type MegaMenuFooterTypes = {
  text: string;
  url: string;
  target?: "_blank";
};

type MegaMenuItemType = {
  text: string;
  url: string;
  placement: "left" | "right" | "full";
  subMenu?: Items[];
};

type CloseButtonProps = {
  type: "text" | "icon";
  placement: "left" | "right";
  icon?: ReactNode;
  text?: ReactNode;
};

type SubscribeboxProps = {
  image?: ImageCrop;
  description: string;
  caption?: string;
  buttonText: string;
  buttonIcon: ReactNode;
  variant?: "primary" | "secondary";
  url: string;
};

type MegaMenuNewsLetterProps = {
  text: string;
  url: string;
  buttonText: string;
};

type MegaMenuBannerProps = {
  heading: string;
  image?: ImageCrop;
  description: string;
  buttonText: string;
  url: string;
};

export type MegaMenuProps = {
  isOpen: boolean;
  toggleSidebar: () => void;
  userSubmissionBox?: UserSubmissionBox;
  SubscribeBoxProps?: SubscribeboxProps;
  NewsLetterProps?: MegaMenuNewsLetterProps;
  BannerProps?: MegaMenuBannerProps;
  FooterItemsProps: MegaMenuFooterTypes[];
  MegaMenuItemsProps: MegaMenuItemType[];
  MegaMenuType: "doubleColumn" | "singleColumn";
  CloseButtonProps?: CloseButtonProps;
  socialIconProps?: SocialIconProps;
  divider?: boolean;
  logoSvg?: string;
  ContentHubTeasers?: GlobalBaseData["navbarDropdown"];
  menuType?: ComponentProps<typeof RenderMegaMenuDoubleColumnItems>["menuType"];
};

function RenderMegaMenuDoubleColumnItems({
  MegaMenuItems,
  menuType = "list",
}: {
  MegaMenuItems: MegaMenuItemType[];
  menuType?: "accordion" | "list";
}) {
  const customClass = useCustomClassContext();
  const [LeftItems, RightItems, FullWidthItems] = MegaMenuItems.reduce<
    [ReactElement[], ReactElement[], ReactElement[]]
  >(
    (acc, item, index) => {
      const Items = (
        <details
          key={index}
          className={cx(styles.MegaMenuColItem, customClass?.megaMenu?.details)}
          open={menuType === "list"}
        >
          <summary
            className={cx(
              styles.MegaMenuColTitle,
              customClass?.megaMenu?.summary?.container,
            )}
          >
            <a
              href={item.url}
              data-layout-position="megamenuitem"
              className={styles.megaMenuColTitleLink}
            >
              {item.text}
            </a>
            {menuType === "accordion" &&
              item.subMenu &&
              item.subMenu?.length > 0 && (
                <span
                  className={cx(customClass?.megaMenu?.summary?.buttonItem)}
                ></span>
              )}
          </summary>

          {item.subMenu?.map(
            (item, id: number) =>
              !item.hideInMegaMenu && (
                <a
                  key={id}
                  href={item.url}
                  className={styles.MegaMenuColSubItem}
                  data-layout-position="megamenuitem"
                >
                  <Body>{item.text}</Body>
                </a>
              ),
          )}
        </details>
      );

      if (item.placement === "left") {
        acc[0].push(Items);
      } else if (item.placement === "right") {
        acc[1].push(Items);
      } else if (item.placement === "full") {
        acc[2].push(Items);
      }
      return acc;
    },
    [[], [], []],
  );

  return (
    <div className={styles.MegaMenuDoubleColItemsContainer}>
      <div className={styles.MegaMenuItemsLeft}>{LeftItems}</div>
      <div className={styles.MegaMenuItemsRight}>{RightItems}</div>
      {FullWidthItems.length > 0 && (
        <div className={styles.MegaMenuItemsFullWidth}>{FullWidthItems}</div>
      )}
    </div>
  );
}

export default function MegaMenu({
  isOpen,
  toggleSidebar,
  CloseButtonProps,
  SubscribeBoxProps,
  NewsLetterProps,
  BannerProps,
  FooterItemsProps,
  MegaMenuItemsProps,
  MegaMenuType,
  userSubmissionBox,
  socialIconProps,
  logoSvg,
  divider = true,
  ContentHubTeasers,
  menuType,
}: MegaMenuProps) {
  const logoSrc =
    logoSvg && `data:image/svg+xml;utf8,${encodeURIComponent(logoSvg)}`;
  const customClass = useCustomClassContext();

  return (
    <div
      data-test-id="mega-menu-container"
      className={cx(
        styles.MegaMenuContainer,
        // styles.isClose,
        isOpen ? styles.isOpen : styles.hide,
      )}
      data-layout-position="megamenu"
    >
      {/* Close Button */}
      <div className={styles.closeButtonContainer}>
        <button onClick={toggleSidebar}>
          {CloseButtonProps?.type === "icon" ? (
            CloseButtonProps.icon || <CloseIcon size={20} strokeWidth={1.5} />
          ) : (
            <div className={styles.closeButtonText}>
              <Body>{CloseButtonProps?.text || "Close"}</Body>
            </div>
          )}
        </button>
      </div>

      {/* Banner */}
      {BannerProps && (
        <div
          className={styles.bannerContainer}
          data-layout-position="megamenublock"
        >
          <Heading.SubHeading2>{BannerProps?.heading}</Heading.SubHeading2>
          <div className={styles.bannerContent}>
            {BannerProps?.image && (
              <div className={styles.bannerImage}>
                <ResponsiveImage {...BannerProps.image} />
              </div>
            )}
            <Body>{BannerProps?.description}</Body>
          </div>
          <div className={styles.bannerButton}>
            <Button href={BannerProps?.url} target="_blank" variant="primary">
              {BannerProps?.buttonText}
            </Button>
          </div>
        </div>
      )}

      {/* Menu Items */}
      {MegaMenuType === "doubleColumn" && (
        <RenderMegaMenuDoubleColumnItems
          MegaMenuItems={MegaMenuItemsProps}
          menuType={menuType}
        />
      )}

      {userSubmissionBox && !userSubmissionBox.skipInMegaMenu && (
        <SponsorshipBox inMenu={true} {...userSubmissionBox} />
      )}

      {/* Content Hub entry point */}
      {ContentHubTeasers?.teasers && ContentHubTeasers?.teasers?.length > 0 && (
        <>
          <Divider />
          <div className={styles.contentHubContainer}>
            <div className={styles.headingContainer}>
              <Heading.SubHeading4>
                {ContentHubTeasers?.titleZH || ContentHubTeasers?.title}
              </Heading.SubHeading4>
            </div>

            <div className={styles.teasersContainer}>
              {ContentHubTeasers?.teasers?.map((item) => {
                const placeholder = placeholderCrops.landscape;
                const landscapeCrop = item.image?.crops.landscape;
                const originalCrop = item.image?.crops.original;

                const imageCrop = landscapeCrop || originalCrop || placeholder;

                return (
                  <a
                    className={styles.teaserItem}
                    href={item.path}
                    key={item.path}
                  >
                    {imageCrop && (
                      <div className={styles.imageContainer}>
                        <ResponsiveImage {...imageCrop} />
                      </div>
                    )}

                    <Body>{item.title}</Body>
                  </a>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Subscribe box */}
      {SubscribeBoxProps && (
        <>
          {(!userSubmissionBox ||
            (userSubmissionBox && userSubmissionBox.skipInMegaMenu)) && (
            <div className={styles.subscribeBoxDivider}>
              <Divider />
            </div>
          )}
          <div
            className={styles.subscribeBoxContainer}
            data-layout-position="megamenublock"
          >
            {SubscribeBoxProps.image && (
              <div className={styles.subscribeBoxImage}>
                <ResponsiveImage
                  {...SubscribeBoxProps.image}
                  displayWidth={98}
                  loading="lazy"
                />
              </div>
            )}
            <div className={styles.subscribeBoxContent}>
              <Body>{SubscribeBoxProps.description}</Body>
              {SubscribeBoxProps.caption && (
                <div className={styles.caption}>
                  <Body>{SubscribeBoxProps.caption}</Body>
                </div>
              )}
              <div className={styles.subscribeBoxButton}>
                <Button
                  href={SubscribeBoxProps.url}
                  variant={SubscribeBoxProps.variant || "primary"}
                  icon={<>{SubscribeBoxProps.buttonIcon}</>}
                  iconPlacement="after"
                  target="_blank"
                >
                  {SubscribeBoxProps.buttonText}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {divider && (
        <div className={styles.subscribeBoxDivider}>
          <Divider />
        </div>
      )}

      {/* NewsLetterbox */}
      {NewsLetterProps && (
        <div
          className={styles.newsletterContainer}
          data-layout-position="megamenublock"
        >
          <Body>{NewsLetterProps.text}</Body>
          <div className={styles.newsLetterButton}>
            <Button href={NewsLetterProps.url} variant="secondary">
              {NewsLetterProps.buttonText}
            </Button>
          </div>
        </div>
      )}

      {/* Footer Items  */}
      <div className={cx(styles.footerContainer)}>
        <div className={cx(styles.MegaMenuFooterItem)}>
          {logoSrc && <img className={styles.logo} src={logoSrc} />}
          {FooterItemsProps.map((item, index: number) => (
            <Body key={index}>
              <a
                href={item.url}
                target={item.target}
                className={styles.footerLink}
              >
                {item.text}
              </a>
            </Body>
          ))}
        </div>
      </div>
      {/* Social Icons */}
      {!!socialIconProps && (
        <div
          className={cx(
            styles.socialIconsContainer,
            customClass?.socialIcons?.megaMenuContainer,
          )}
        >
          <SocialIcons {...socialIconProps} />
        </div>
      )}
    </div>
  );
}
